import React, { useState, useEffect } from 'react';
import { ModuleTempGraph, SensorHealthBadge, BlockTempDrilldown } from '../components/WeatherDiagnostics';
import { weatherService } from '../services/weatherService';

interface Plant {
  id: string;
  name: string;
}

export const WeatherDiagnostics: React.FC = () => {
  const [selectedPlant, setSelectedPlant] = useState<string>('1');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [plants, setPlants] = useState<Plant[]>([]);
  const [drilldownBlock, setDrilldownBlock] = useState<string | null>(null);
  const [showDrilldown, setShowDrilldown] = useState(false);

  useEffect(() => {
    // Mock plants data - in real app, fetch from API
    setPlants([
      { id: '1', name: 'GHCL 10MW' },
      { id: '2', name: 'Solar Park A' },
      { id: '3', name: 'Solar Park B' }
    ]);
  }, []);

  const handleBlockDrilldown = (blockId: string) => {
    setDrilldownBlock(blockId);
    setShowDrilldown(true);
  };

  const closeDrilldown = () => {
    setShowDrilldown(false);
    setDrilldownBlock(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Environmental Diagnostics & Sensor Health
          </h1>
          <p className="text-gray-600">
            Monitor module temperature trends and sensor health across blocks for environmental impact analysis
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plant
              </label>
              <select
                value={selectedPlant}
                onChange={(e) => setSelectedPlant(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {plants.map((plant) => (
                  <option key={plant.id} value={plant.id}>
                    {plant.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metric
              </label>
              <select
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
              >
                <option>Module Temperature</option>
              </select>
            </div>
          </div>
        </div>

        {/* Module Temperature Analysis - Full Width */}
        <div className="mb-6">
          <ModuleTempGraph 
            plantId={selectedPlant} 
            date={selectedDate}
          />
        </div>

        {/* Sensor Health Monitor - Full Width Below */}
        <div className="mb-6">
          <SensorHealthBadge plantId={selectedPlant} />
        </div>

        {/* Intelligence Layer Insights */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Flatline Detector</h4>
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            </div>
            <p className="text-sm text-gray-600">
              Monitoring for sensors with static readings over time
            </p>
            <div className="mt-2 text-xs text-green-600">
              ✓ All sensors responding normally
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Temp-Yield Correlation</h4>
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing temperature impact on specific yield
            </p>
            <div className="mt-2 text-xs text-blue-600">
              📊 Correlation analysis active
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Environmental Drift</h4>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            </div>
            <p className="text-sm text-gray-600">
              Detecting abnormal temperature deviations
            </p>
            <div className="mt-2 text-xs text-yellow-600">
              ⚠ Minor deviations detected in Block-15
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Predictive Maintenance</h4>
              <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
            </div>
            <p className="text-sm text-gray-600">
              Flagging sensors for maintenance needs
            </p>
            <div className="mt-2 text-xs text-purple-600">
              🔧 2 sensors scheduled for maintenance
            </div>
          </div>
        </div>

        {/* Standards Compliance */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Standards Alignment & Compliance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">IEC 61724</div>
              <div className="text-sm text-gray-600">PV System Performance</div>
              <div className="text-xs text-green-600 mt-1">✓ Compliant</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">IEEE 1547</div>
              <div className="text-sm text-gray-600">Interconnection Standards</div>
              <div className="text-xs text-green-600 mt-1">✓ Compliant</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">ISO 50001</div>
              <div className="text-sm text-gray-600">Energy Management</div>
              <div className="text-xs text-green-600 mt-1">✓ Compliant</div>
            </div>
          </div>
        </div>

        {/* Block Temperature Drilldown Modal */}
        {drilldownBlock && (
          <BlockTempDrilldown
            isOpen={showDrilldown}
            onClose={closeDrilldown}
            plantId={selectedPlant}
            blockId={drilldownBlock}
            date={selectedDate}
          />
        )}
      </div>
    </div>
  );
};